import { useLazyQuery, useMutation } from '@apollo/client'
import { useTheme } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import apolloClient from '../../graphql/apolloClient'
import { GATEWAY_MODIFY_MUTATION, GATEWAY_QUERY } from '../../graphql/gateways'
import { gatewayInput } from '../../helpers/gatewayPaymentType'
import { Gateway, GatewayPaymentType } from '../../types/gateway'
import GatewayBasicInfo from './GatewayBasicInfo'
import GatewayCredential from './GatewayCredential'
import { gatewaysFormValidationSchema } from './validations/gatewaysFormValidation'

const GATEWAY_INIT_VALUES: Gateway = {
  basicDetails: {
    entity: '',
    entityType: 'Accommodation',
    gatewayId: '',
    name: '',
    entityId: '',
    supportedCurrencies: [],
    description: '',
    entityCode: '',
    displayEntityCode: '',
    active: true,
    walletType: ''
  },
  credential: {
    credentialMode: '',
    gatewaysType: ''
  },
  credentialModeDetails: {
    apiKey: '',
    merchantId: '',
    password: '',
    terminalId: '',
    username: ''
  }
}

interface GateWayMainFormProps {
  setNameForBreadcrumbs: React.Dispatch<React.SetStateAction<string>>
}

const GateWayMainForm: React.FC<GateWayMainFormProps> = ({
  setNameForBreadcrumbs
}) => {
  const theme = useTheme()
  const { id } = useParams<{ id: string }>()

  const [initialValues, setinitialValues] =
    useState<Gateway>(GATEWAY_INIT_VALUES)

  const [getInitialValues, { loading }] = useLazyQuery<{ gateway: Gateway }>(
    GATEWAY_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  // The payment type the server holds: set on load and after each successful save. An
  // edit sends the payment type only when it differs from this, so an untouched one is
  // never resent, and a change back after an earlier save is still sent.
  const savedPaymentType = useRef<GatewayPaymentType | undefined>()

  const getInitialValuesFunction = async () => {
    const { data } = await getInitialValues({ variables: { gatewayId: id } })
    savedPaymentType.current = data?.gateway?.basicDetails.paymentType
    setinitialValues(data?.gateway!)
  }

  useEffect(() => {
    getInitialValuesFunction()
  }, [])

  const [gatewayOnSubmitFunction, { loading: loadingForSubmit }] = useMutation(
    GATEWAY_MODIFY_MUTATION
  )

  const handleSubmit = async (values: Gateway) => {
    try {
      const { data: response } = await gatewayOnSubmitFunction({
        variables: {
          input: gatewayInput(values, savedPaymentType.current),
          gatewayID: id
        }
      })
      savedPaymentType.current =
        values.basicDetails.paymentType ?? savedPaymentType.current

      toast.success(response.modifyGateway.message)
      apolloClient.cache.evict({
        fieldName: 'gateways'
      })
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('ERROR!')
      }
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={gatewaysFormValidationSchema}>
      {({
        values,
        setFieldValue,
        errors,
        touched,
        handleBlur,
        isSubmitting
      }) => {
        return (
          <>
            <Form>
              <GatewayBasicInfo
                values={values}
                setFieldValue={setFieldValue}
                errors={errors}
                touched={touched}
                handleBlur={handleBlur}
                isSubmitting={isSubmitting}
                setNameForBreadcrumbs={setNameForBreadcrumbs}
              />
              <GatewayCredential
                values={values}
                setFieldValue={setFieldValue}
                errors={errors}
                touched={touched}
                handleBlur={handleBlur}
                isSubmitting={isSubmitting}
                loadingForSubmit={loadingForSubmit}
              />
            </Form>
            <AdotelSkeleton
              open={loading}
              circlesColor={theme.palette.primary.main}
              numberOfCircles={4}
            />
          </>
        )
      }}
    </Formik>
  )
}
export default GateWayMainForm
